import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { apiCallBegan, ApiCallFailedPayload } from "../api";
import { authHeader } from "../users/auth";
import i18n from "../../i18n";
import ResponseBase from "../types/ResponseBase";
import SliceCollectionBase from "../types/SliceCollectionBase";
import Company from "../../entities/Company";
import { AppDispatch, AppState } from "../configureStore";

// RESPONSE DECLARATIONS //
interface CompanyResponse extends ResponseBase {
  Code: string;
  CountryCode: string;
  Name: string;
}

type ClassificatorResponse<T> = T[] | { value: T[] };

// SLICE //
interface State extends SliceCollectionBase<Company> {
  logo: null | string;
}

const initialState: State = {
  list: [],
  loading: false,
  lastFetch: null,
  error: null,
  logo: null
}

const slice = createSlice({
  name: "companies",
  initialState,
  reducers: {
    //LOGO
    companyLogoReceived: (companies, action: PayloadAction<string>) => {
      companies.logo = action.payload;
      companies.loading = false;
      companies.lastFetch = Date.now();
    },

    //COMPANIES
    companiesRequested: (companies) => {
      companies.loading = true;
      companies.error = null;
    },
    companiesReceived: (companies, action: PayloadAction<ClassificatorResponse<CompanyResponse>>) => {
      companies.list = schema(unwrapClassificatorResponse(action.payload));
      companies.loading = false;
      companies.lastFetch = Date.now();
    },
    companiesRequestFailed: (companies, action: PayloadAction<ApiCallFailedPayload>) => {
      companies.loading = false;
      companies.error = action.payload;
    }
  }
});

const {
  companyLogoReceived,

  companiesRequested,
  companiesReceived,
  companiesRequestFailed
} = slice.actions;

export default slice.reducer;

// ACTION CREATORS //
export const getCompanyLogo = () => async (dispatch: AppDispatch, getState: () => AppState) => {
  //@ts-ignore
  const { CompanyId } = getState().users.auth.data;
  const url = "/api/Tenant/logo/" + CompanyId;

  return await dispatch(
    apiCallBegan({
      url,
      method: "GET",
      headers: {
        ...authHeader(),
        "Accept-Language": i18n.language
      },
      onSuccess: companyLogoReceived.type,
      responseType: "blob"
    })
  );
};

export const getCompanies = () => async (dispatch: AppDispatch, getState: () => AppState) => {
  const { isLoggedIn } = getState().users.auth;
  const { loading } = getState().entities.companies;

  if (!isLoggedIn || loading) {
    return;
  }

  const url = `/api/classificator/companies`;

  return await dispatch(
    apiCallBegan({
      url,
      method: "GET",
      headers: {
        ...authHeader(),
        "Accept-Language": i18n.language
      },
      onStart: companiesRequested.type,
      onSuccess: companiesReceived.type,
      onError: companiesRequestFailed.type
    })
  );
};

export const getCompanyById = (state:AppState, id?: number): Company | null => {
  return state.entities.companies.list.find((company) => company.Id === id) || null;
}

// NORMALIZATION //
const schema = (payload: CompanyResponse[]): Company[] => {
  if (!payload || payload.length === 0) {
    return [];
  }
  return payload.map((resp: CompanyResponse): Company => (
    {
      CountryCode : resp.CountryCode, 
      Code: resp.Code,
      Id: resp.Id,
      Name: resp.Name
    }
  ))
};

const unwrapClassificatorResponse = <T>(payload: ClassificatorResponse<T>): T[] => {
  if (Array.isArray(payload)) {
    return payload;
  }

  return payload?.value || [];
};

